import React, { useEffect, useState } from 'react';
import {
    Webhook,
    Plus,
    RefreshCw,
    Trash2,
    Copy,
    Check,
    Eye,
    EyeOff,
    Pencil,
    Power,
    Send,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
} from 'lucide-react';
import webhooksService from '../services/webhooks';
import { useApps } from '../contexts/AppsContext';
import PageHeader from '../components/common/PageHeader';
import SkeletonList from '../components/common/SkeletonList';
import ErrorState from '../components/common/ErrorState';
import Modal from '../components/common/Modal';
import { isValidWebhookUrl, webhookUrlHintText } from '../utils/webhookUrl';
import { resolveDefaultAppId, setSelectedAppId } from '../utils/storage';

const WebhooksPage = () => {
    const { apps, loading: loadingApps } = useApps();
    const [appId, setAppId] = useState('');
    const [webhooks, setWebhooks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [newSecretData, setNewSecretData] = useState(null);
    const [copiedField, setCopiedField] = useState('');
    const [showSecret, setShowSecret] = useState(false);
    const [editingWebhook, setEditingWebhook] = useState(null);
    const [expandedEvents, setExpandedEvents] = useState(null);
    const [expandedDeliveries, setExpandedDeliveries] = useState(null);
    const [deliveries, setDeliveries] = useState([]);
    const [deliveriesLoading, setDeliveriesLoading] = useState(false);
    const [deliveriesPage, setDeliveriesPage] = useState(1);
    const [deliveriesTotalPages, setDeliveriesTotalPages] = useState(1);
    const [formData, setFormData] = useState({
        webhook_url: '',
        events: '',
        description: '',
        tenant_id: '',
    });

    useEffect(() => {
        if (apps?.length > 0) {
            const defaultAppId = resolveDefaultAppId(apps);
            setAppId(defaultAppId);
            loadWebhooks(defaultAppId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apps]);

    const loadWebhooks = async (targetAppId) => {
        if (!targetAppId) return;
        setLoading(true);
        setError('');
        try {
            const data = await webhooksService.list(targetAppId);
            setWebhooks(data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load webhooks');
        } finally {
            setLoading(false);
        }
    };

    const handleAppChange = (e) => {
        const id = e.target.value;
        setAppId(id);
        setSelectedAppId(id);
        if (id) loadWebhooks(id);
    };

    const resetForm = () => {
        setFormData({ webhook_url: '', events: '', description: '', tenant_id: '' });
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError('');
        if (!isValidWebhookUrl(formData.webhook_url)) {
            setError(webhookUrlHintText());
            return;
        }
        try {
            const payload = {
                app_id: appId,
                webhook_url: formData.webhook_url,
                events: formData.events
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
            };
            if (formData.description) payload.description = formData.description;
            if (formData.tenant_id) payload.tenant_id = formData.tenant_id;
            const res = await webhooksService.register(payload);
            setNewSecretData(res.data);
            setShowCreateModal(false);
            resetForm();
            loadWebhooks(appId);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to register webhook');
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setError('');
        if (!isValidWebhookUrl(formData.webhook_url)) {
            setError(webhookUrlHintText());
            return;
        }
        try {
            const payload = {
                webhook_id: editingWebhook.webhook_id,
                webhook_url: formData.webhook_url,
                events: formData.events
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                description: formData.description || undefined,
            };
            await webhooksService.update(payload);
            setShowEditModal(false);
            setEditingWebhook(null);
            resetForm();
            setSuccessMessage('Webhook updated.');
            loadWebhooks(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to update webhook');
        }
    };

    const openEditModal = (wh) => {
        setEditingWebhook(wh);
        setFormData({
            webhook_url: wh.webhook_url,
            events: Array.isArray(wh.events) ? wh.events.join(', ') : wh.events || '',
            description: wh.description || '',
            tenant_id: wh.tenant_id || '',
        });
        setShowEditModal(true);
    };

    const handleToggleActive = async (wh) => {
        const newStatus = wh.is_active ? 0 : 1;
        const action = wh.is_active ? 'deactivate' : 'activate';
        if (!window.confirm(`Are you sure you want to ${action} this webhook?`)) return;
        try {
            await webhooksService.update({ webhook_id: wh.webhook_id, is_active: newStatus });
            setSuccessMessage(`Webhook ${action}d.`);
            loadWebhooks(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || `Failed to ${action} webhook`);
        }
    };

    const handleRotateSecret = async (webhookId) => {
        if (!window.confirm("Rotate this webhook's secret? The old secret will stop working immediately.")) return;
        try {
            const res = await webhooksService.rotateSecret({ webhook_id: webhookId });
            setNewSecretData(res.data);
            setSuccessMessage('Secret rotated!');
            loadWebhooks(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to rotate secret');
        }
    };

    const handleTest = async (webhookId) => {
        try {
            await webhooksService.test({ webhook_id: webhookId });
            setSuccessMessage('Test event sent successfully!');
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Test event failed');
        }
    };

    const handleDelete = async (webhookId) => {
        if (!window.confirm('Delete this webhook? It will be removed immediately.')) return;
        try {
            await webhooksService.deleteWebhook({ webhook_id: webhookId });
            setSuccessMessage('Webhook deleted.');
            loadWebhooks(appId);
            setTimeout(() => setSuccessMessage(''), 3000);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to delete webhook');
        }
    };

    const loadDeliveries = async (webhookId, page = 1) => {
        setDeliveriesLoading(true);
        try {
            const res = await webhooksService.getDeliveries(webhookId, { page, limit: 10 });
            setDeliveries(res?.data?.deliveries || res?.data || []);
            setDeliveriesTotalPages(res?.data?.totalPages || 1);
            setDeliveriesPage(page);
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed to load deliveries');
        } finally {
            setDeliveriesLoading(false);
        }
    };

    const toggleDeliveries = (webhookId) => {
        if (expandedDeliveries === webhookId) {
            setExpandedDeliveries(null);
            setDeliveries([]);
        } else {
            setExpandedDeliveries(webhookId);
            loadDeliveries(webhookId, 1);
        }
    };

    const copyToClipboard = (text, field) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };

    const truncateUrl = (url, maxLen = 40) => {
        if (!url || url.length <= maxLen) return url;
        return url.substring(0, maxLen) + '...';
    };

    const parseEvents = (events) => {
        if (Array.isArray(events)) return events;
        if (typeof events === 'string') {
            try {
                return JSON.parse(events);
            } catch {
                return events.split(',').map((s) => s.trim());
            }
        }
        return [];
    };

    const getStatusColor = (status, response_status) => {
        if (status === 'success') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
        if (status === 'failed') return 'text-red-700 bg-red-50 border-red-100';
        return 'text-slate-500 bg-slate-50 border-slate-200';
    };

    return (
        <div className="space-y-4">
            <PageHeader
                title="Webhooks"
                subtitle="Manage webhook subscriptions for real-time event notifications"
                actions={
                    <div className="flex gap-2">
                        <select
                            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 min-w-[200px]"
                            value={appId}
                            onChange={handleAppChange}
                            disabled={loadingApps}
                        >
                            <option value="">Select an App</option>
                            {apps.map((app) => (
                                <option key={app.app_id} value={app.app_id}>
                                    {app.app_name}
                                </option>
                            ))}
                        </select>
                        {appId && (
                            <button
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Register Webhook
                            </button>
                        )}
                    </div>
                }
            />

            {successMessage && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 text-sm">
                    {successMessage}
                </div>
            )}

            {/* New Secret Display (shown after create/rotate) */}
            {newSecretData && (
                <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-5 space-y-3">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                        <Webhook className="h-4 w-4" />
                        Save this secret — it won't be shown again!
                    </div>
                    <div className="space-y-2">
                        {newSecretData.webhook_id && (
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-500 w-24">Webhook ID:</span>
                                <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                    {newSecretData.webhook_id}
                                </code>
                                <button
                                    onClick={() => copyToClipboard(newSecretData.webhook_id, 'id')}
                                    className="p-1 rounded hover:bg-amber-100"
                                >
                                    {copiedField === 'id' ? (
                                        <Check className="h-4 w-4 text-emerald-600" />
                                    ) : (
                                        <Copy className="h-4 w-4 text-slate-500" />
                                    )}
                                </button>
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-500 w-24">Secret:</span>
                            <code className="flex-1 bg-white rounded px-2 py-1 text-sm font-mono border border-amber-200">
                                {showSecret ? newSecretData.secret : '\u2022'.repeat(40)}
                            </code>
                            <button
                                onClick={() => setShowSecret(!showSecret)}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {showSecret ? (
                                    <EyeOff className="h-4 w-4 text-slate-500" />
                                ) : (
                                    <Eye className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                            <button
                                onClick={() => copyToClipboard(newSecretData.secret, 'secret')}
                                className="p-1 rounded hover:bg-amber-100"
                            >
                                {copiedField === 'secret' ? (
                                    <Check className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <Copy className="h-4 w-4 text-slate-500" />
                                )}
                            </button>
                        </div>
                    </div>
                    <button
                        onClick={() => {
                            setNewSecretData(null);
                            setShowSecret(false);
                        }}
                        className="text-xs text-amber-700 hover:underline"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {loading ? (
                <SkeletonList />
            ) : error && !showCreateModal && !showEditModal ? (
                <ErrorState message={error} />
            ) : !appId ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <p className="text-slate-500">Select an App to manage webhooks.</p>
                </div>
            ) : webhooks.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-white/90 p-8 text-center">
                    <Webhook className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No webhooks yet. Register one to receive event notifications.</p>
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-white/90 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 bg-slate-50/50">
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">URL</th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">Events</th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">Status</th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">Failures</th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">Last Triggered</th>
                                    <th className="text-right px-4 py-3 font-medium text-slate-600">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {webhooks.map((wh) => {
                                    const events = parseEvents(wh.events);
                                    const displayEvents = events.slice(0, 2);
                                    const hasMore = events.length > 2;
                                    return (
                                        <React.Fragment key={wh.webhook_id}>
                                            <tr className="border-b border-slate-100 hover:bg-slate-50/50">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <Webhook className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                                        <div>
                                                            <div
                                                                className="font-mono text-xs text-slate-700"
                                                                title={wh.webhook_url}
                                                            >
                                                                {truncateUrl(wh.webhook_url)}
                                                            </div>
                                                            {wh.description && (
                                                                <div className="text-xs text-slate-400 mt-0.5">
                                                                    {wh.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-wrap gap-1">
                                                        {displayEvents.map((ev, i) => (
                                                            <span
                                                                key={i}
                                                                className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100"
                                                            >
                                                                {ev}
                                                            </span>
                                                        ))}
                                                        {hasMore && (
                                                            <button
                                                                onClick={() =>
                                                                    setExpandedEvents(
                                                                        expandedEvents === wh.webhook_id
                                                                            ? null
                                                                            : wh.webhook_id
                                                                    )
                                                                }
                                                                className="inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
                                                            >
                                                                +{events.length - 2} more
                                                                {expandedEvents === wh.webhook_id ? (
                                                                    <ChevronUp className="h-3 w-3 ml-0.5" />
                                                                ) : (
                                                                    <ChevronDown className="h-3 w-3 ml-0.5" />
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {expandedEvents === wh.webhook_id && hasMore && (
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {events.slice(2).map((ev, i) => (
                                                                <span
                                                                    key={i}
                                                                    className="inline-block text-xs font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-100"
                                                                >
                                                                    {ev}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                                                            wh.is_active
                                                                ? 'text-emerald-700 bg-emerald-50 border-emerald-100'
                                                                : 'text-slate-500 bg-slate-50 border-slate-200'
                                                        }`}
                                                    >
                                                        {wh.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span
                                                        className={`text-xs font-medium ${
                                                            wh.failure_count > 0 ? 'text-red-600' : 'text-slate-400'
                                                        }`}
                                                    >
                                                        {wh.failure_count || 0}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-xs text-slate-500">
                                                    {wh.last_triggered_at
                                                        ? new Date(wh.last_triggered_at).toLocaleString()
                                                        : 'Never'}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <button
                                                            onClick={() => openEditModal(wh)}
                                                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            title="Edit"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleToggleActive(wh)}
                                                            className={`p-1.5 rounded-md hover:bg-slate-100 ${
                                                                wh.is_active
                                                                    ? 'text-emerald-600 hover:text-emerald-700'
                                                                    : 'text-slate-400 hover:text-slate-600'
                                                            }`}
                                                            title={wh.is_active ? 'Deactivate' : 'Activate'}
                                                        >
                                                            <Power className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleRotateSecret(wh.webhook_id)}
                                                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            title="Rotate Secret"
                                                        >
                                                            <RefreshCw className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleTest(wh.webhook_id)}
                                                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                                                            title="Test"
                                                        >
                                                            <Send className="h-3.5 w-3.5" />
                                                        </button>
                                                        <button
                                                            onClick={() => toggleDeliveries(wh.webhook_id)}
                                                            className={`p-1.5 rounded-md hover:bg-slate-100 ${
                                                                expandedDeliveries === wh.webhook_id
                                                                    ? 'text-primary-600 bg-primary-50'
                                                                    : 'text-slate-500 hover:text-slate-700'
                                                            }`}
                                                            title="View Deliveries"
                                                        >
                                                            {expandedDeliveries === wh.webhook_id ? (
                                                                <ChevronUp className="h-3.5 w-3.5" />
                                                            ) : (
                                                                <ChevronDown className="h-3.5 w-3.5" />
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(wh.webhook_id)}
                                                            className="p-1.5 rounded-md text-red-500 hover:bg-red-50 hover:text-red-700"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                            {/* Delivery Log Expandable Row */}
                                            {expandedDeliveries === wh.webhook_id && (
                                                <tr>
                                                    <td colSpan={6} className="px-4 py-3 bg-slate-50/80">
                                                        <div className="space-y-2">
                                                            <div className="text-xs font-semibold text-slate-600">
                                                                Delivery Log
                                                            </div>
                                                            {deliveriesLoading ? (
                                                                <div className="text-xs text-slate-400 py-4 text-center">
                                                                    Loading deliveries...
                                                                </div>
                                                            ) : deliveries.length === 0 ? (
                                                                <div className="text-xs text-slate-400 py-4 text-center">
                                                                    No deliveries recorded yet.
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <table className="w-full text-xs">
                                                                        <thead>
                                                                            <tr className="border-b border-slate-200">
                                                                                <th className="text-left py-2 px-2 font-medium text-slate-500">
                                                                                    Event Type
                                                                                </th>
                                                                                <th className="text-left py-2 px-2 font-medium text-slate-500">
                                                                                    Status
                                                                                </th>
                                                                                <th className="text-left py-2 px-2 font-medium text-slate-500">
                                                                                    Response
                                                                                </th>
                                                                                <th className="text-left py-2 px-2 font-medium text-slate-500">
                                                                                    Attempts
                                                                                </th>
                                                                                <th className="text-left py-2 px-2 font-medium text-slate-500">
                                                                                    Timestamp
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {deliveries.map((d, idx) => (
                                                                                <tr
                                                                                    key={d.delivery_id || idx}
                                                                                    className="border-b border-slate-100"
                                                                                >
                                                                                    <td className="py-1.5 px-2 font-mono">
                                                                                        {d.event_type}
                                                                                    </td>
                                                                                    <td className="py-1.5 px-2">
                                                                                        <span
                                                                                            className={`inline-block px-1.5 py-0.5 rounded-full border text-xs font-medium ${getStatusColor(
                                                                                                d.status
                                                                                            )}`}
                                                                                        >
                                                                                            {d.status}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="py-1.5 px-2 font-mono">
                                                                                        {d.response_status || '—'}
                                                                                    </td>
                                                                                    <td className="py-1.5 px-2">
                                                                                        {d.attempt_count || 1}
                                                                                    </td>
                                                                                    <td className="py-1.5 px-2 text-slate-500">
                                                                                        {d.created_at
                                                                                            ? new Date(
                                                                                                  d.created_at
                                                                                              ).toLocaleString()
                                                                                            : '—'}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                    {/* Pagination */}
                                                                    {deliveriesTotalPages > 1 && (
                                                                        <div className="flex items-center justify-between pt-2">
                                                                            <span className="text-xs text-slate-500">
                                                                                Page {deliveriesPage} of{' '}
                                                                                {deliveriesTotalPages}
                                                                            </span>
                                                                            <div className="flex gap-1">
                                                                                <button
                                                                                    disabled={deliveriesPage <= 1}
                                                                                    onClick={() =>
                                                                                        loadDeliveries(
                                                                                            wh.webhook_id,
                                                                                            deliveriesPage - 1
                                                                                        )
                                                                                    }
                                                                                    className="p-1 rounded-md text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                                >
                                                                                    <ChevronLeft className="h-4 w-4" />
                                                                                </button>
                                                                                <button
                                                                                    disabled={
                                                                                        deliveriesPage >=
                                                                                        deliveriesTotalPages
                                                                                    }
                                                                                    onClick={() =>
                                                                                        loadDeliveries(
                                                                                            wh.webhook_id,
                                                                                            deliveriesPage + 1
                                                                                        )
                                                                                    }
                                                                                    className="p-1 rounded-md text-slate-500 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
                                                                                >
                                                                                    <ChevronRight className="h-4 w-4" />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Create Webhook Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                title="Register Webhook"
                size="sm"
            >
                <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Webhook URL <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="url"
                            required
                            value={formData.webhook_url}
                            onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="https://example.com/webhooks"
                        />
                        <p className="text-xs text-slate-400 mt-1">Must start with https://</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Events <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.events}
                            onChange={(e) => setFormData({ ...formData, events: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="delegation.*, group.*, user.created"
                        />
                        <p className="text-xs text-slate-400 mt-1">Comma-separated event patterns</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                        <input
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="e.g., Production event handler"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Tenant (optional)</label>
                        <input
                            type="text"
                            value={formData.tenant_id}
                            onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="Tenant ID"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Register
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowCreateModal(false)}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>

            {/* Edit Webhook Modal */}
            <Modal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingWebhook(null);
                }}
                title="Edit Webhook"
                size="sm"
            >
                <form onSubmit={handleEdit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Webhook URL <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="url"
                            required
                            value={formData.webhook_url}
                            onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="https://example.com/webhooks"
                        />
                        <p className="text-xs text-slate-400 mt-1">Must start with https://</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                            Events <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            required
                            value={formData.events}
                            onChange={(e) => setFormData({ ...formData, events: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="delegation.*, group.*, user.created"
                        />
                        <p className="text-xs text-slate-400 mt-1">Comma-separated event patterns</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                        <input
                            type="text"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                            placeholder="e.g., Production event handler"
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3 pt-4 border-t border-slate-200">
                        <button
                            type="submit"
                            className="flex-1 rounded-lg bg-primary-600 text-white font-medium py-2.5 hover:bg-primary-700"
                        >
                            Save Changes
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setShowEditModal(false);
                                setEditingWebhook(null);
                            }}
                            className="flex-1 rounded-lg border border-slate-200 text-slate-700 font-medium py-2.5 hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </Modal>
        </div>
    );
};

export default WebhooksPage;
